import { KeyRound, Lock, LockOpen, Trash, Users } from 'lucide-react';
import type { ComponentProps } from 'react';
import type { SanitizedUser } from '@movo/contracts/api/users';
import type { UserRole } from '@movo/contracts/domain/user';
import ListPagination from '@/shared/ui/fleet-system/ListPagination';
import StatusBadge from '@/shared/ui/fleet-system/StatusBadge';
import { controlWithWidth, secondaryButtonClass } from '@/shared/ui/formClasses';
import type { RowPermissions } from '../model/permissions';
import { getRoleLabel } from '../model/roles';
import RoleOptions from './RoleOptions';

const headClass = 'px-3 py-2.5 text-left text-field-label text-subtle';
const cellClass = 'px-3 py-2.5 text-sm text-default';

// Il backend serve al massimo 50 utenti per pagina.
const PAGE_SIZES = [10, 20, 50];

type UserRow = RowPermissions & { user: SanitizedUser };

type RowActions = {
  onChangeRole: (id: string, role: UserRole) => void;
  onToggleActive: (user: SanitizedUser) => void;
  onSetPassword: (user: SanitizedUser) => void;
  onDelete: (user: SanitizedUser) => void;
};

type UsersTableProps = RowActions & {
  rows: UserRow[];
  busy: boolean;
  pagination: Omit<ComponentProps<typeof ListPagination>, 'title' | 'pageSizeLabel' | 'pageSizes'>;
};

function UserRowView({
  row,
  onChangeRole,
  onToggleActive,
  onSetPassword,
  onDelete,
}: RowActions & { row: UserRow }) {
  const { user, isSelf, canUpdate, canDelete, canChangeRole, roleOptions } = row;
  const active = user.status.isActive;
  const toggleLabel = active ? 'Disattiva' : 'Attiva';

  return (
    <tr className="transition-colors hover:bg-hover/65">
      <td className={cellClass}>
        <span className="font-semibold text-strong">{user.username}</span>
        {isSelf && <span className="ml-1 text-caption text-subtle">(tu)</span>}
      </td>
      <td className={cellClass}>{user.email}</td>
      <td className={cellClass}>
        {canChangeRole ? (
          <select
            aria-label={`Ruolo di ${user.username}`}
            value={user.role}
            // Le opzioni sono i ruoli di `roleOptions`: il DOM restituisce solo stringhe.
            onChange={(e) => onChangeRole(user._id, e.target.value as UserRole)}
            className={controlWithWidth('w-auto')}
          >
            <RoleOptions options={roleOptions} />
          </select>
        ) : (
          getRoleLabel(user.role)
        )}
      </td>
      <td className={cellClass}>
        <StatusBadge tone={active ? 'success' : 'neutral'}>
          {active ? 'Attivo' : 'Disattivo'}
        </StatusBadge>
      </td>
      <td className={`${cellClass} text-right`}>
        <div className="flex flex-wrap justify-end gap-2">
          {canUpdate && (
            <>
              <button
                type="button"
                onClick={() => onToggleActive(user)}
                aria-label={`${toggleLabel} ${user.username}`}
                className={secondaryButtonClass}
              >
                {active ? (
                  <Lock aria-hidden className="h-3.5 w-3.5" />
                ) : (
                  <LockOpen aria-hidden className="h-3.5 w-3.5" />
                )}
                {toggleLabel}
              </button>
              <button
                type="button"
                onClick={() => onSetPassword(user)}
                aria-label={`Imposta la password di ${user.username}`}
                className={secondaryButtonClass}
              >
                <KeyRound aria-hidden className="h-3.5 w-3.5" />
                Password
              </button>
            </>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={() => onDelete(user)}
              aria-label={`Elimina ${user.username}`}
              className={secondaryButtonClass}
            >
              <Trash aria-hidden className="h-3.5 w-3.5" />
              Elimina
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

/** Gli utenti della pagina con le azioni che i permessi concedono, e la paginazione. */
export default function UsersTable({ rows, busy, pagination, ...actions }: UsersTableProps) {
  return (
    <div
      aria-busy={busy}
      className="overflow-hidden rounded-xl border border-default bg-default shadow-resting"
    >
      <div className="overflow-x-auto">
        <table aria-label="Utenti" className="min-w-full divide-y divide-subtle">
          <thead className="bg-subtle">
            <tr>
              <th scope="col" className={headClass}>
                Nome
              </th>
              <th scope="col" className={headClass}>
                Email
              </th>
              <th scope="col" className={headClass}>
                Ruolo
              </th>
              <th scope="col" className={headClass}>
                Stato
              </th>
              <th scope="col" className={headClass}>
                <span className="sr-only">Azioni</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-subtle">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center">
                  <Users aria-hidden className="mx-auto h-8 w-8 text-placeholder" />
                  <p className="mt-2 text-card-title text-strong">Nessun utente trovato</p>
                  <p className="mt-1 text-caption text-subtle">Cambia la ricerca o i filtri.</p>
                </td>
              </tr>
            ) : (
              rows.map((row) => <UserRowView key={row.user._id} row={row} {...actions} />)
            )}
          </tbody>
        </table>
      </div>

      <ListPagination
        title="Lista utenti"
        pageSizeLabel="Utenti per pagina"
        pageSizes={PAGE_SIZES}
        {...pagination}
      />
    </div>
  );
}
